import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface TourStep {
  id: string;
  selector: string;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  showNext: boolean;
  showPrev: boolean;
  showSkip: boolean;
  action?: () => void;
  beforeShow?: () => void;
  afterShow?: () => void;
}

export interface TourConfig {
  id: string;
  name: string;
  steps: TourStep[];
}

@Injectable({
  providedIn: 'root'
})
export class TourGuideService {
  private currentTour: TourConfig | null = null;
  private currentStepIndex = 0;
  private isActive = false;

  private tourStateSubject = new BehaviorSubject<{
    isActive: boolean;
    currentStep: TourStep | null;
    stepIndex: number;
    totalSteps: number;
  }>({
    isActive: false,
    currentStep: null,
    stepIndex: 0,
    totalSteps: 0
  });

  public tourState$ = this.tourStateSubject.asObservable();

  startTour(tourConfig: TourConfig): void {
    this.currentTour = tourConfig;
    this.currentStepIndex = 0;
    this.isActive = true;
    this.showCurrentStep();
  }

  nextStep(): void {
    if (!this.currentTour) return;
    
    if (this.currentStepIndex >= this.currentTour.steps.length - 1) {
      // Ultimo step - termina il tour ma mantieni l'indice per il messaggio di completamento
      this.isActive = false;
      this.tourStateSubject.next({
        isActive: false,
        currentStep: null,
        stepIndex: this.currentStepIndex,
        totalSteps: this.currentTour.steps.length
      });
      return;
    }
    
    this.currentStepIndex++;
    this.showCurrentStep();
  }

  prevStep(): void {
    if (this.currentStepIndex <= 0) return;
    this.currentStepIndex--;
    this.showCurrentStep();
  }

  skipTour(): void {
    this.endTour();
  }

  endTour(): void {
    this.isActive = false;
    this.currentTour = null;
    this.currentStepIndex = 0;
    this.tourStateSubject.next({
      isActive: false,
      currentStep: null,
      stepIndex: 0,
      totalSteps: 0
    });
  }

  private showCurrentStep(): void {
    if (!this.currentTour) return;

    const currentStep = this.currentTour.steps[this.currentStepIndex];
    
    // Esegui azione prima di mostrare lo step
    if (currentStep.beforeShow) {
      currentStep.beforeShow();
    }

    this.tourStateSubject.next({
      isActive: true,
      currentStep: currentStep,
      stepIndex: this.currentStepIndex,
      totalSteps: this.currentTour.steps.length
    });

    // Scrolla all'elemento target
    setTimeout(() => {
      const element = document.querySelector(currentStep.selector);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);

    // Esegui azione dopo aver mostrato lo step
    if (currentStep.afterShow) {
      setTimeout(() => currentStep.afterShow!(), 200);
    }
  }

  getCurrentStep(): TourStep | null {
    return this.currentTour?.steps[this.currentStepIndex] || null;
  }

  getCurrentTour(): TourConfig | null {
    return this.currentTour;
  }

  goToStep(stepIndex: number): void {
    if (!this.currentTour || stepIndex < 0 || stepIndex >= this.currentTour.steps.length) {
      return;
    }
    this.currentStepIndex = stepIndex;
    this.showCurrentStep();
  }
}
